// CSEMPE-GALÉRIA („Csempe hozzáadása"): jobb oldali Sheet (mobilon teljes képernyő). CSAK a jogosult ÉS még nem kirakott
// csempéket listázza (`availableTiles`) kategóriánként — ikon, cím, leírás, „Új" jelvény (a mentett katalógus-revízió
// óta megjelent csempe) és „Hozzáadás" gomb. Jog nélküli csempe nincs a listán (nem „tiltott", hanem nincs ott).
import {
  availableTiles,
  isNewTile,
  type DashboardTileCategory,
  type DashboardTileDef,
} from '@atlas/shared';
import { PlusIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/shared/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Text } from '@/components/ui/text';
import { tileIcon } from '@/features/dashboard/tiles/tile-icon';

const CATEGORY_ORDER: DashboardTileCategory[] = ['kpi', 'work', 'finance', 'team', 'personal'];

export interface TileGallerySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A már kirakott (a draftban lévő) csempék id-i. */
  placedIds: readonly string[];
  can: (permission: string) => boolean;
  /** A mentett katalógus-revízió („Új" jelvényhez). */
  catalogRev: number;
  onAdd: (id: string) => void;
}

export function TileGallerySheet({
  open,
  onOpenChange,
  placedIds,
  can,
  catalogRev,
  onAdd,
}: TileGallerySheetProps): JSX.Element {
  const { t } = useTranslation();
  const available = availableTiles(placedIds, can);
  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    tiles: available.filter((d) => d.category === category),
  })).filter((g) => g.tiles.length > 0);

  const add = (def: DashboardTileDef): void => {
    onAdd(def.id);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideClose
        aria-describedby={undefined}
        className="text-foreground gap-0 bg-[var(--app-surface-glass)] p-0"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 px-5 py-4">
          <SheetTitle className="min-w-0 truncate">{t('dashboard.gallery.title')}</SheetTitle>
          <SheetClose className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mr-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1">
            <XMarkIcon className="size-4" />
            <span className="sr-only">{t('common.close')}</span>
          </SheetClose>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-6">
          {groups.length === 0 ? (
            <EmptyState
              title={t('dashboard.gallery.emptyTitle')}
              description={t('dashboard.gallery.emptyDescription')}
            />
          ) : (
            groups.map((g) => (
              <section key={g.category} className="space-y-2">
                <Text variant="overline" as="h3">
                  {t(`dashboard.gallery.categories.${g.category}`)}
                </Text>
                <ul className="space-y-2">
                  {g.tiles.map((def) => {
                    const Icon = tileIcon(def);
                    return (
                      <li
                        key={def.id}
                        data-testid={`gallery-${def.id}`}
                        className="card-glass flex items-start gap-3 rounded-xl border p-3"
                      >
                        <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-xl">
                          <Icon className="size-5" aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-md text-foreground font-semibold">
                              {t(def.titleKey)}
                            </span>
                            {isNewTile(def, catalogRev) && (
                              <Badge variant="brand">{t('dashboard.gallery.new')}</Badge>
                            )}
                          </div>
                          <p className="text-muted-foreground text-sm">{t(def.descriptionKey)}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => add(def)}
                          aria-label={t('dashboard.gallery.addTile', { tile: t(def.titleKey) })}
                        >
                          <PlusIcon aria-hidden />
                          {t('dashboard.gallery.add')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
